import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from './apiBaseUrl';
import { invalidateSuperAdminDashboardCache } from '../utils/saDashboardCache';
import {
  clearAuthenticationState,
  getAccessToken,
  refreshAccessToken,
} from './authService';

const axiosInstance = axios.create({
  // baseURL is validated for HTTPS in production (see secureTransport.ts).
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

function getRefreshPromise(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

axiosInstance.interceptors.response.use(
  (response) => {
    // Any successful write makes the cached dashboard counts stale.
    const method = String(response.config?.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head' && method !== 'options') {
      invalidateSuperAdminDashboardCache();
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const requestUrl = originalRequest.url || '';
    if (requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const newToken = await getRefreshPromise();
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      clearAuthenticationState();
      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;
